import { useEffect, useRef, useState, type RefObject } from 'react';

export interface Taille {
  largeur: number;
  hauteur: number;
}

/**
 * Mesure un conteneur et suit ses changements de taille.
 *
 * Les graphes et la carte sont rendus en SVG à des dimensions réelles, jamais mis à l'échelle
 * par un `viewBox` étiré : une déformation non uniforme épaissirait les traits d'un côté et les
 * amincirait de l'autre, et rendrait les libellés d'axe illisibles.
 */
export function useTaille<T extends HTMLElement>(): [RefObject<T>, Taille] {
  const reference = useRef<T>(null);
  const [taille, setTaille] = useState<Taille>({ largeur: 0, hauteur: 0 });

  useEffect(() => {
    const element = reference.current;
    if (!element) return undefined;

    const mesurer = (): void => {
      const rect = element.getBoundingClientRect();
      setTaille((precedente) => {
        const largeur = Math.round(rect.width);
        const hauteur = Math.round(rect.height);
        if (precedente.largeur === largeur && precedente.hauteur === hauteur) return precedente;
        return { largeur, hauteur };
      });
    };

    mesurer();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', mesurer);
      return () => {
        window.removeEventListener('resize', mesurer);
      };
    }

    const observateur = new ResizeObserver(mesurer);
    observateur.observe(element);
    return () => {
      observateur.disconnect();
    };
  }, []);

  return [reference, taille];
}
